import React, { memo, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Dashboard from '../pages/Dashboard';
import NotFound from '../component/pageNotFound';
import UserManagementPage from '../pages/UserManagement';
import EmployeeManagement from '../pages/EmployeeManagement';
import PromosTransactions from '../pages/PromosTransactions';
import PettyCash from '../pages/PettyCash';
import PositionTipsRecords from '../pages/PositionTipsRecords';
import PlayerProfileModule from '../pages/PlayerProfileModulePage';
import TournamentPokerChips from '../pages/TournamenPokerChips';
import PokerCashBox from '../pages/PokerCashBox';
import TokenReserves from '../pages/TokenReserves';
import TournamentHouseFees from '../pages/TournamentHouseFees';
import StartingClosingTablesTime from '../pages/StartingClosingTablesTime';
import TournamentLeaderboardPage from '../pages/TournamentLeaderboardPage';
import SalesAnalytics from '../pages/SalesAnalytics';
import SmsLogsPage from '../pages/SmsLogsPage';
import TaxManagement from '../pages/TaxManagement';

const SALES_ANALYTICS_PATH = '/dashboard/payroll/sales-analytics';


/** Stable trees — sidebar location changes must not re-render heavy charts. */
const KeptAliveDashboard = memo(function KeptAliveDashboard() {
  return <Dashboard />;
});

const KeptAliveSalesAnalytics = memo(function KeptAliveSalesAnalytics() {
  return <SalesAnalytics />;
});

/**
 * Keeps heavy analytics pages mounted after first visit so sidebar navigation
 * does not tear down charts / Redux-hydrated UI.
 */
const AppMainContent = () => {
  const location = useLocation();
  const isDashboardAnalytics = location.pathname === '/';
  const isSalesAnalytics = location.pathname === SALES_ANALYTICS_PATH;

  const hasVisitedDashboardRef = useRef(isDashboardAnalytics);
  const hasVisitedSalesRef = useRef(isSalesAnalytics);

  useEffect(() => {
    if (isDashboardAnalytics) {
      hasVisitedDashboardRef.current = true;
      const id = window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [isDashboardAnalytics]);

  useEffect(() => {
    if (isSalesAnalytics) {
      hasVisitedSalesRef.current = true;
      const id = window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [isSalesAnalytics]);

  const keepDashboardMounted = hasVisitedDashboardRef.current || isDashboardAnalytics;
  const keepSalesMounted = hasVisitedSalesRef.current || isSalesAnalytics;

  return (
    <>
      {keepDashboardMounted && (
        <div
          style={{ display: isDashboardAnalytics ? 'block' : 'none' }}
          aria-hidden={!isDashboardAnalytics}
        >
          <KeptAliveDashboard />
        </div>
      )}

      {keepSalesMounted && (
        <div
          style={{ display: isSalesAnalytics ? 'block' : 'none' }}
          aria-hidden={!isSalesAnalytics}
        >
          <KeptAliveSalesAnalytics />
        </div>
      )}

      <Routes>
        <Route path="/" element={keepDashboardMounted ? null : <Dashboard />} />
        <Route path="/user-management" element={<UserManagementPage />} />
        <Route path="/employee-management" element={<EmployeeManagement />} />
        <Route
          path={SALES_ANALYTICS_PATH}
          element={keepSalesMounted ? null : <SalesAnalytics />}
        />
        <Route path="/promos" element={<PromosTransactions />} />
        <Route path="/petty-cash" element={<PettyCash />} />
        <Route path="/dealer-tips" element={<PositionTipsRecords position="Dealer" />} />
        <Route path="/waitress-tips" element={<PositionTipsRecords position="Server/waitress" />} />
        <Route path="/server-tips" element={<PositionTipsRecords position="Server/waitress" />} />
        <Route path="/floor-tips" element={<PositionTipsRecords position="Floor Supervisor" />} />
        <Route path="/floor-supervisor-tips" element={<PositionTipsRecords position="Floor Supervisor" />} />
        <Route path="/bartender-tips" element={<PositionTipsRecords position="Bartender" />} />
        <Route path="/host-tips" element={<PositionTipsRecords position="Host" />} />
        <Route path="/cashier-tips" element={<PositionTipsRecords position="Cashier" />} />
        <Route path="/janitor-tips" element={<PositionTipsRecords position="Custodian/Janitor" />} />
        <Route path="/custodian-tips" element={<PositionTipsRecords position="Custodian/Janitor" />} />
        <Route path="/custodian-janitor-tips" element={<PositionTipsRecords position="Custodian/Janitor" />} />
        <Route path="/cook-tips" element={<PositionTipsRecords position="Cook" />} />
        <Route path="/tax-management" element={<TaxManagement />} />
        <Route path="/role-management" element={<Navigate to="/user-management" replace />} />
        <Route path="/other-tips" element={<PositionTipsRecords position="Other" />} />

        <Route path="/player-profiles" element={<PlayerProfileModule />} />
        <Route path="/tournament-poker-chips" element={<TournamentPokerChips />} />
        <Route path="/poker-cash-box" element={<PokerCashBox />} />
        <Route path="/tournament-house-fees" element={<TournamentHouseFees />} />
        <Route path="/token-reserves" element={<TokenReserves />} />
        <Route path="/starting-closing-tables-time" element={<StartingClosingTablesTime />} />
        <Route path="/sms-logs" element={<SmsLogsPage />} />
        <Route path="/tournament-leaderboard" element={<TournamentLeaderboardPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

export default AppMainContent;
